import constants from "../constants";
import { sendToErrorTracker } from "./error";
import { ILog, LogType, Source } from "./logger";
import { getNextSequence, uploadToBigQueryPredicate } from "./logger.internal";

const DB_NAME = "loggingDb";
const DB_VERSION = 1;
const LOGS_STORE = "logs";
const STALE_TRANSACTION_ERROR_SUFFIX = "without an in-progress transaction";
// Delays (ms) before each successive retry. Gives the iOS Networking process time
// to restart after being suspended. Index 0 = delay before retry 1, index 1 = before retry 2.
const RETRY_DELAYS_MS = [300, 500];
const MAX_FALLBACK_LOGS = 100;

let dbPromise: Promise<IDBDatabase> | null = null;
let closingPromise: Promise<void> | null = null;
// In-memory buffer for logs that could not be written after all retries. Drained on
// the next successful addLogToDb call.
let fallbackLogs: ILog[] = [];

/** Exposed for tests only — resets the in-memory fallback buffer. */
export function clearFallbackLogs(): void {
    fallbackLogs = [];
}

/** Exposed for tests only — returns the current fallback buffer length. */
export function getFallbackLogCount(): number {
    return fallbackLogs.length;
}

/**
 * Atomically removes all entries from the fallback buffer and returns them.
 * The caller is responsible for restoring entries on failure via restoreFallbackLogs.
 */
export function drainFallbackLogs(): ILog[] {
    return fallbackLogs.splice(0);
}

/**
 * Prepends logs back to the front of the fallback buffer (used to undo a drain on upload failure).
 * Excess entries beyond MAX_FALLBACK_LOGS are dropped to respect the cap.
 */
export function restoreFallbackLogs(logs: ILog[]): void {
    fallbackLogs = [...logs, ...fallbackLogs].slice(0, MAX_FALLBACK_LOGS);
}

function isStaleConnectionError(error: unknown): boolean {
    if (error instanceof DOMException) {
        if (error.name === "InvalidStateError"
            || error.name === "TransactionInactiveError"
            || error.name === "AbortError") {
            return true;
        }
        // WebKit surfaces stale transaction errors as UnknownError with a common message suffix.
        if (error.name === "UnknownError" && error.message.includes(STALE_TRANSACTION_ERROR_SUFFIX)) {
            return true;
        }
    }
    return false;
}

function resetConnection() {
    if (dbPromise) {
        closingPromise = dbPromise.then(db => db.close()).catch(() => { /* already closed */ });
    }
    dbPromise = null;
}

function promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function promisifyTransaction(tx: IDBTransaction): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = (event) => {
            // The actual error lives on the failed request (event.target), not on tx.error,
            // which may not be set until after the transaction finishes.
            const requestError = (event.target as IDBRequest)?.error;
            reject(requestError || tx.error || new Error("IndexedDB transaction threw an error."));
        };
        tx.onabort = () => {
            // When a transaction is aborted externally (e.g. iOS WebKit suspending the process),
            // tx.error is null. Use a DOMException with AbortError so isStaleConnectionError
            // can detect it and trigger a retry with a fresh connection.
            reject(tx.error || new DOMException("IndexedDB transaction was aborted.", "AbortError"));
        };
    });
}

function openDb(): Promise<IDBDatabase> {
    try {
        if (dbPromise) {
            return dbPromise;
        }
        // Wait for any in-flight close to finish before opening a new connection,
        // otherwise the new open may contend with the pending close on some browsers.
        const pending = closingPromise || Promise.resolve();
        closingPromise = null;
        dbPromise = pending.then(() => new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(LOGS_STORE)) {
                    db.createObjectStore(LOGS_STORE, { autoIncrement: true });
                }

            };
            request.onsuccess = () => {
                const db = request.result;
                // Reset cached promise if the browser closes the connection,
                // so the next operation will re-open it.
                db.onclose = () => { resetConnection(); };
                resolve(db);
            };
            request.onerror = () => reject(request.error);
        }));
        return dbPromise;
    } catch (error) {
        throw new Error(`Failed to open DB ${ error }`);
    }
}

// Drains the in-memory fallback buffer into IDB. Called after every successful write.
// Best-effort: if IDB is still unavailable, unprocessed logs are returned to the front
// of the buffer and we stop until the next successful write.
async function flushFallbackLogs(): Promise<void> {
    if (fallbackLogs.length === 0) {
        return;
    }
    const toFlush = fallbackLogs.splice(0);
    for (let i = 0; i < toFlush.length; i++) {
        try {
            const db = await openDb();
            const tx = db.transaction(LOGS_STORE, "readwrite");
            tx.objectStore(LOGS_STORE).add(toFlush[i]);
            await promisifyTransaction(tx);
        } catch (error) {
            // IDB still unavailable — restore remaining entries and stop.
            fallbackLogs = [...toFlush.slice(i), ...fallbackLogs];
            // Clear a broken cached connection so the next addLogToDb gets a fresh one.
            if (isStaleConnectionError(error)) {
                resetConnection();
            }
            break;
        }
    }
}

export async function addLogToDb(log: ILog, retryCount = 0): Promise<void> {
    try {
        const db = await openDb();
        const tx = db.transaction(LOGS_STORE, "readwrite");
        const store = tx.objectStore(LOGS_STORE);

        store.add(log);
        await promisifyTransaction(tx);
        // Success — drain any logs buffered during previous IDB failures.
        void flushFallbackLogs();
    } catch (error) {
        const delayMs = RETRY_DELAYS_MS[retryCount];
        if (delayMs !== undefined && isStaleConnectionError(error)) {
            resetConnection();
            await new Promise<void>(r => setTimeout(r, delayMs));
            return addLogToDb(log, retryCount + 1);
        }
        // All retries exhausted — buffer for the next flush opportunity instead of throwing.
        // Logging is non-critical; a throw here would surface as an unhandled rejection
        // because callers fire-and-forget. The log will be persisted once IDB recovers.
        // eslint-disable-next-line no-console
        console.warn(`Failed to persist log to IndexedDB after retries: ${ error }`);
        if (fallbackLogs.length < MAX_FALLBACK_LOGS) {
            fallbackLogs.push(log);
        }
    }
}

export async function getAllLogsAndKeysFromDb(limit?: number, retryCount = 0) {
    try {
        const db = await openDb();

        const tx = db.transaction(LOGS_STORE, "readonly");
        const store = tx.objectStore(LOGS_STORE);
        // Use the helper to promisify requests and run them in parallel
        const logsPromise = promisifyRequest<any[]>(store.getAll(null, limit));
        const keysPromise = promisifyRequest<IDBValidKey[]>(store.getAllKeys(null, limit));

        // Wait for both promises to complete
        const [logs, keys] = await Promise.all([logsPromise, keysPromise]);

        return { logs, keys };
    } catch (error) {
        const delayMs = RETRY_DELAYS_MS[retryCount];
        if (delayMs !== undefined && isStaleConnectionError(error)) {
            resetConnection();
            await new Promise<void>(r => setTimeout(r, delayMs));
            return getAllLogsAndKeysFromDb(limit, retryCount + 1);
        }
        throw new Error(`Failed to get all logs from DB ${ error }`);
    }
}

export async function getLogsByTypesAndSources(limit: number, retryCount = 0) {
    try {
        const db = await openDb();
        const tx = db.transaction(LOGS_STORE, "readonly");
        const store = tx.objectStore(LOGS_STORE);

        return await new Promise<{logs: ILog[]; keys: IDBValidKey[]}>((resolve, reject) => {
            const request = store.openCursor();
            const logs: ILog[] = [];
            const keys: IDBValidKey[] = [];

            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;
                if (cursor && logs.length < limit) {
                    const log = cursor.value as ILog;
                    if (uploadToBigQueryPredicate(log)) {
                        logs.push(log);
                        keys.push(cursor.primaryKey);
                    }
                    cursor.continue();
                } else {
                    resolve({ logs, keys });
                }
            };
            request.onerror = () => reject(request.error);
        });
    } catch (error) {
        const delayMs = RETRY_DELAYS_MS[retryCount];
        if (delayMs !== undefined && isStaleConnectionError(error)) {
            resetConnection();
            await new Promise<void>(r => setTimeout(r, delayMs));
            return getLogsByTypesAndSources(limit, retryCount + 1);
        }
        throw new Error(`Failed to get logs by types and sources from DB ${ error }`);
    }
}

export async function clearAllUploadedKeysFromDb(keys: IDBValidKey[] = [], retryCount = 0) {
    if (keys.length === 0) {
        return; // Nothing to do
    }

    try {
        const db = await openDb();
        const tx = db.transaction(LOGS_STORE, "readwrite");
        const store = tx.objectStore(LOGS_STORE);

        // Queue all the delete operations within the transaction.
        // We don't need to await each one, as they all run as part of the transaction.
        for (const key of keys) {
            store.delete(key);
        }

        // Await the completion of the entire transaction using our helper.
        // This single line replaces the entire new Promise() block.
        await promisifyTransaction(tx);
    } catch (error) {
        const delayMs = RETRY_DELAYS_MS[retryCount];
        if (delayMs !== undefined && isStaleConnectionError(error)) {
            resetConnection();
            await new Promise<void>(r => setTimeout(r, delayMs));
            return clearAllUploadedKeysFromDb(keys, retryCount + 1);
        }
        // The error from the promisified transaction will be caught here.
        throw new Error(`Failed to clear uploaded logs from DB: ${ error }`);
    }
}

export async function clearAllLogsFromDb(retryCount = 0) {
    try {
        const db = await openDb();
        const tx = db.transaction(LOGS_STORE, "readwrite");
        const store = tx.objectStore(LOGS_STORE);

        store.clear();

        await promisifyTransaction(tx);
    } catch (error) {
        const delayMs = RETRY_DELAYS_MS[retryCount];
        if (delayMs !== undefined && isStaleConnectionError(error)) {
            resetConnection();
            await new Promise<void>(r => setTimeout(r, delayMs));
            return clearAllLogsFromDb(retryCount + 1);
        }
        sendToErrorTracker(error, "javascript");
    }
}

export async function trimLogsInDb(retryCount = 0, user: IUser | null) {
    try {
        const db = await openDb();

       // Transaction 1 — count
        const countTx = db.transaction(LOGS_STORE, "readonly");
        const countStore = countTx.objectStore(LOGS_STORE);
        const countReq = countStore.count();
        await promisifyTransaction(countTx);
        const count = countReq.result;   // safe: transaction has committed

        const deleteCount = count - constants.LOGGING.LENGTH;

        if (deleteCount <= 0) {
            return 0;
        }
        // Transaction 2 — keys
        const keysTx = db.transaction(LOGS_STORE, "readonly");
        const keysStore = keysTx.objectStore(LOGS_STORE);
        const keysReq = keysStore.getAllKeys(null, deleteCount);
        await promisifyTransaction(keysTx);
        const keys = keysReq.result;

        // Transaction 3: Delete keys
        if (keys.length > 0) {
            const deleteTx = db.transaction(LOGS_STORE, "readwrite");
            const deleteStore = deleteTx.objectStore(LOGS_STORE);

            for (const key of keys) {
                deleteStore.delete(key);
            }
            await promisifyTransaction(deleteTx);
        }

        return deleteCount;
    } catch (error) {
        const delayMs = RETRY_DELAYS_MS[retryCount];
        if (delayMs !== undefined && isStaleConnectionError(error)) {
            resetConnection();
            await new Promise<void>(r => setTimeout(r, delayMs));
            return trimLogsInDb(retryCount + 1, user);
        }
        if (fallbackLogs.length < MAX_FALLBACK_LOGS) {
            const errorLog: ILog = {
                type: LogType[LogType.ERROR],
                date: new Date().toISOString(),
                source: Source[Source.INDEXED_DB],
                message: String(error),
                user: user?.name?.toLowerCase() ?? "",
                instanceId: window?.driveApp?.store?.getState()?.data?.instanceId ?? "",
                sequence: getNextSequence()
            };
            fallbackLogs.push(errorLog);
        }
        return -1;
    }
}
